import React, { useEffect, useState, useRef } from 'react';
import RedactorFileUpload from '../components/RedactorFileUpload';
import RedactorSettings from '../components/RedactorSettings';
import RedactorProgress from '../components/RedactorProgress';
import RedactorResultsViewer from '../components/RedactorResultsViewer';
import redactorService from '../services/redactorService';

/**
 * RedactorPage – The Redactor
 *
 * Workflow:
 *  1. Upload files (PDF, DOCX, TXT – up to 20)
 *  2. Select which PII categories to redact
 *  3. Run redaction, view progress
 *  4. Review results: preview original vs redacted, view match summary,
 *     manually add / remove redactions, download individual or all files
 */

const STEPS = {
  UPLOAD: 'upload',
  SETTINGS: 'settings',
  PROCESSING: 'processing',
  RESULTS: 'results',
};

const DEFAULT_CATEGORIES = [
  'full_ssn',
  'full_account_number',
  'ein',
  'itin',
  'ptin',
  'personal_phone',
  'personal_email',
];

const POLL_INTERVAL_MS = 5000;
const HIDDEN_POLL_INTERVAL_MS = 15000;

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg).filter(Boolean).join(' ') || fallback;
  }
  return fallback;
};

function RedactorPage() {
  // Workflow state
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [files, setFiles] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(DEFAULT_CATEGORIES);
  const [useOcr, setUseOcr] = useState(true);
  const [jobId, setJobId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [jobStatus, setJobStatus] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Ref to stop in-flight polling when the user resets
  const pollingRef = useRef(false);

  useEffect(() => () => {
    pollingRef.current = false;
  }, []);

  // Step 1 → 2: Upload files to backend
  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const resp = await redactorService.uploadFiles(files);
      setJobId(resp.job_id);
      setUploadedFiles(resp.files);
      setStep(STEPS.SETTINGS);
    } catch (err) {
      console.error('Upload error:', err);
      setError(getErrorMessage(err, 'File upload failed.'));
    } finally {
      setUploading(false);
    }
  };

  // Step 2 → 3: Start redaction – returns 202 immediately, then polls
  const handleStartRedaction = async () => {
    if (selectedCategories.length === 0) return;
    setStep(STEPS.PROCESSING);
    setError(null);
    setJobStatus({ status: 'processing', progress: 0, files: [] });
    pollingRef.current = true;

    try {
      // 202 response with initial placeholder data
      const initial = await redactorService.startRedaction(jobId, selectedCategories, useOcr);
      setJobStatus(initial);

      while (pollingRef.current) {
        const pollInterval = document.hidden
          ? HIDDEN_POLL_INTERVAL_MS
          : POLL_INTERVAL_MS;
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        if (!pollingRef.current) break;
        const poll = await redactorService.getJobStatus(jobId);
        setJobStatus(poll);
        if (poll.status === 'completed' || poll.status === 'failed') {
          pollingRef.current = false;
          if (poll.status === 'completed') {
            setStep(STEPS.RESULTS);
          } else {
            setError(poll.error || 'Redaction failed.');
          }
          break;
        }
      }
    } catch (err) {
      pollingRef.current = false;
      console.error('Redaction error:', err);
      const message = getErrorMessage(err, 'Redaction failed.');
      setJobStatus({
        status: 'failed',
        progress: 0,
        files: [],
        error: message,
      });
      setError(message);
    }
  };

  // Reset to start over
  const handleReset = () => {
    pollingRef.current = false;
    setStep(STEPS.UPLOAD);
    setFiles([]);
    setSelectedCategories(DEFAULT_CATEGORIES);
    setUseOcr(true);
    setJobId(null);
    setUploadedFiles([]);
    setJobStatus(null);
    setError(null);
  };

  return (
    <div className="container-fluid py-3">
      {/* Header: title | step progress | start over */}
      <div className="d-flex align-items-center justify-content-between mb-4 gap-3 flex-wrap">
        <div style={{ minWidth: 220 }}>
          <h3 className="mb-1">
            <i className="bi bi-shield-lock-fill me-2 text-primary"></i>
            The Redactor
          </h3>
          <small className="text-muted">
            Upload documents and automatically redact sensitive personal information.
          </small>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-center">
          {[
            { key: STEPS.UPLOAD, label: 'Upload Files', icon: 'bi-cloud-upload' },
            { key: STEPS.SETTINGS, label: 'Settings', icon: 'bi-sliders' },
            { key: STEPS.PROCESSING, label: 'Processing', icon: 'bi-gear-wide-connected' },
            { key: STEPS.RESULTS, label: 'Results', icon: 'bi-file-earmark-check' },
          ].map((s, i) => {
            const stepOrder = [STEPS.UPLOAD, STEPS.SETTINGS, STEPS.PROCESSING, STEPS.RESULTS];
            const currentIdx = stepOrder.indexOf(step);
            const thisIdx = stepOrder.indexOf(s.key);
            const isActive = step === s.key;
            const isDone = thisIdx < currentIdx;

            return (
              <React.Fragment key={s.key}>
                {i > 0 && (
                  <i className={`bi bi-chevron-right ${isDone || isActive ? 'text-primary' : 'text-muted'}`}></i>
                )}
                <div
                  className={`badge rounded-pill px-3 py-2 ${
                    isActive ? 'bg-primary' : isDone ? 'bg-success' : 'bg-secondary bg-opacity-25 text-muted'
                  }`}
                >
                  <i className={`bi ${isDone ? 'bi-check-lg' : s.icon} me-1`}></i>
                  {s.label}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div style={{ minWidth: 100, textAlign: 'right' }}>
          {step !== STEPS.UPLOAD && (
            <button className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
              <i className="bi bi-arrow-counterclockwise me-1"></i>Start Over
            </button>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && step !== STEPS.PROCESSING && (
        <div className="alert alert-danger alert-dismissible py-2" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Step content */}
      <div className="card">
        <div className="card-body">
          {/* STEP 1: Upload */}
          {step === STEPS.UPLOAD && (
            <>
              <RedactorFileUpload
                files={files}
                setFiles={setFiles}
                disabled={uploading}
              />
              <div className="d-flex justify-content-end">
                <button
                  className="btn btn-primary"
                  disabled={files.length === 0 || uploading}
                  onClick={handleUpload}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      Continue <i className="bi bi-arrow-right ms-1"></i>
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {/* STEP 2: Settings */}
          {step === STEPS.SETTINGS && (
            <>
              <div className="alert alert-info py-2 mb-3">
                <i className="bi bi-info-circle me-2"></i>
                {uploadedFiles.length} file(s) uploaded. Select the types of information to redact.
              </div>
              <RedactorSettings
                selected={selectedCategories}
                setSelected={setSelectedCategories}
                useOcr={useOcr}
                setUseOcr={setUseOcr}
                disabled={false}
              />
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setStep(STEPS.UPLOAD)}
                >
                  <i className="bi bi-arrow-left me-1"></i>Back
                </button>
                <button
                  className="btn btn-primary"
                  disabled={selectedCategories.length === 0}
                  onClick={handleStartRedaction}
                >
                  <i className="bi bi-play-fill me-1"></i>
                  Start Redaction
                </button>
              </div>
            </>
          )}

          {/* STEP 3: Processing */}
          {step === STEPS.PROCESSING && (
            <RedactorProgress jobStatus={jobStatus} />
          )}

          {/* STEP 4: Results */}
          {step === STEPS.RESULTS && (
            <RedactorResultsViewer
              jobId={jobId}
              files={jobStatus?.files || []}
              onRefresh={async () => {
                const resp = await redactorService.getJobStatus(jobId);
                setJobStatus(resp);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default RedactorPage;
